import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  LayoutDashboard, Package, Users, LogOut, ArrowLeft, Tag, List, 
  Settings2, ShieldCheck, Truck, History, ShoppingCart, 
  UserRound, Wallet, ClipboardCheck, FileText, BarChart3, 
  CreditCard, Search, Activity, Building2, 
  PlusCircle, Briefcase, Layers, PieChart, RefreshCcw,
  Edit3, MapPin, ArrowRightLeft, TrendingUp, ChevronUp, ChevronDown, PackageCheck,
  Bed, Menu, X, Lock, Settings, UserCheck2, GraduationCap,
  Utensils // 🚀 Ajouté ici
} from 'lucide-react';
import API from '../services/api';

const Sidebar = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname;
  const scrollRef = useRef(null);
  
  // État pour ouvrir/fermer la sidebar sur mobile
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const companyName = localStorage.getItem('companyName') || 'Ledi Expert Pro';
  const isAdmin = (user.role || '').toLowerCase() === 'admin' || (user.role || '').toLowerCase() === 'super_admin';

  // ======================================================
  // LICENCE : SOURCE UNIQUE = SERVEUR
  // ======================================================
  const [licenseData, setLicenseData] = useState({
    allowed_modules: [],
    valid: false
  });

  const fetchSidebarLicense = useCallback(async () => {
    try {
      const res = await API.get(`/license/status?t=${Date.now()}`);
      const data = res.data || {};
      let mods = data.allowed_modules || data.mod || [];
      if (typeof mods === 'string') {
        try {
          mods = JSON.parse(mods);
        } catch (e) {
          mods = mods.replace(/[\[\]"']/g, '').split(',').map(m => m.trim().toUpperCase()).filter(Boolean);
        }
      }
      if (Array.isArray(mods)) {
        mods = mods.map(m => String(m).replace(/[\[\]"']/g, '').trim().toUpperCase()).filter(Boolean);
      } else {
        mods = [];
      }
      const isLicenseValid = data.valid === true || data.valid === 'true' || data.valid === 1 || data.valid === '1' || data.isActive === true || data.isActive === 1 || data.success === true;
      setLicenseData({
        ...data,
        allowed_modules: mods,
        valid: isLicenseValid
      });
    } catch (error) {
      console.error("❌ Impossible de récupérer le statut de la licence :", error);
      setLicenseData({ allowed_modules: [], valid: false });
    }
  }, []);

  useEffect(() => {
    fetchSidebarLicense();
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchSidebarLicense();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [fetchSidebarLicense, path]);

  // Fermer automatiquement le menu mobile lors d'un changement de route
  useEffect(() => {
    setIsMobileOpen(false);
  }, [path]);

  // ======================================================
  // NOUVELLE ARCHITECTURE DE PERMISSIONS STRICTE
  // ======================================================
  const canAccessModule = useCallback((moduleKey) => {
    if (!moduleKey) return true;
    const targetModule = String(moduleKey).toUpperCase();
    if (targetModule === 'SYSTEM') return true;
    const mods = (licenseData?.allowed_modules || []).map(m => m.toUpperCase());
    return mods.includes(targetModule) || mods.includes('FULL_ACCESS');
  }, [licenseData]);

  const hasPermission = useCallback((permissionKey) => {
    if (isAdmin) return true;
    if (!permissionKey) return false;
    let perms = user.permissions;
    while (typeof perms === 'string' && perms.trim() !== "") {
      try { perms = JSON.parse(perms); } catch (e) { return false; }
    }
    if (!perms || typeof perms !== 'object') return false;
    const value = perms[permissionKey];
    return value === true || value === 1 || value === 'true' || value === '1';
  }, [isAdmin, user.permissions]);

  const canShow = useCallback((permKey, moduleKey) => {
    if (!canAccessModule(moduleKey)) return false;
    return hasPermission(permKey);
  }, [canAccessModule, hasPermission]);

  // --- DÉTECTIONS DES RECOUPEMENTS DE SECTIONS ---
  const isCaisseSection = path.startsWith('/pos/close') || path.startsWith('/pos/historique-cloture') || path.startsWith('/pos/paiements/methodes');
  const isVenteSection = (path.startsWith('/pos') || path.startsWith('/ventes')) && !isCaisseSection;
  const isArticleSection = path.startsWith('/articles') || path.startsWith('/articles/history') || path.startsWith('/articles');
  const isLogistiqueSection = path.startsWith('/logistique');
  const isRHSection = path.startsWith('/admin/staff') || path.startsWith('/admin/users') || path.startsWith('/rh') || path.startsWith('/users') || path.startsWith('/staff');
  const isSettingsSection = path.startsWith('/admin/settings') || path.startsWith('/audit') || path.startsWith('/params') || path.startsWith('/admin/licence') || path.startsWith('/settings');
  const isComptaSection = path.startsWith('/compta');
  const isEmballageSection = path.startsWith('/emballage') || path.startsWith('/emballages');
  const isAnalytiqueSection = path.startsWith('/analytique');
  const isScolaireSection = path.startsWith('/scolaire');
  const isHebergementSection = path.startsWith('/hebergement');
  const isTiersSection = path.startsWith('/Tiers') || path.startsWith('/tiers');
  const isRapportsManagerSection = path.startsWith('/rapports');

  // --- PERSISTANCE DU SCROLL SIDEBAR ---
  useEffect(() => {
    const savedScroll = sessionStorage.getItem('sidebar-scroll');
    if (savedScroll && scrollRef.current) {
      requestAnimationFrame(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = parseInt(savedScroll, 10);
        }
      });
    }
  }, [path]);

  const handleScroll = () => {
    if (scrollRef.current) {
      sessionStorage.setItem('sidebar-scroll', scrollRef.current.scrollTop);
    }
  };

  const scrollManual = (direction) => {
    if (scrollRef.current) {
      const amount = direction === 'up' ? -200 : 200;
      scrollRef.current.scrollBy({ top: amount, behavior: 'smooth' });
    }
  };

  const [isPushing, setIsPushing] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);

  const handleManualPush = async () => {
    setIsPushing(true);
    try { console.log("Sauvegarde en cours..."); } finally { setIsPushing(false); }
  };

  const handleCloudRestore = async () => {
    setIsRestoring(true);
    try { console.log("Restauration en cours..."); } finally { setIsRestoring(false); }
  };

  const goTo = (route) => navigate(route);
  const activeClass = (route) => (path === route ? 'active' : '');

  const BackBtn = () => (
    <button className="nav-item back-button" onClick={() => goTo('/admin/dashboard')} 
      style={{color: '#6366f1', borderBottom: '1px solid #f1f5f9', marginBottom: '15px', borderRadius: '0', width: '100%', background: 'transparent'}}>
      <div className="nav-item-content">
        <ArrowLeft size={18} />
        <span style={{fontWeight: 'bold', fontSize: '11px'}}>
          {t('sidebar.back_to_menu', 'RETOUR AU MENU')}
        </span>
      </div>
    </button>
  );

  const renderArticleMenu = () => (
    canAccessModule('GESTOCK') && (
      <>
        <BackBtn />
        <div className="menu-group-label">{t('sidebar.article_manager', 'Gestionnaire Articles')}</div>
        {canShow('art_categories', 'GESTOCK') && (<button className={`nav-item ${activeClass('/articles/categories')}`} onClick={() => goTo('/articles/categories')}><span>{t('sidebar.categories_groups', 'Familles Catégories & Groupes')}</span></button>)}
        {canShow('art_create', 'GESTOCK') && (<button className={`nav-item ${activeClass('/articles/create')}`} onClick={() => goTo('/articles/create')}><span>{t('sidebar.add_article', 'Ajouter un Article')}</span></button>)}
        {canShow('art_mod', 'GESTOCK') && (<button className={`nav-item ${activeClass('/articles')}`} onClick={() => goTo('/articles')}><span>{t('sidebar.mod_article', 'Modifier un Article')}</span></button>)}
        {canShow('art_list', 'GESTOCK') && (<button className={`nav-item ${activeClass('/articles/list')}`} onClick={() => goTo('/articles/list')}><span>{t('sidebar.articles_list', 'Liste des Articles')}</span></button>)}
        {canShow('art_view', 'GESTOCK') && (<button className={`nav-item ${activeClass('/articles/unites')}`} onClick={() => goTo('/articles/unites')}><span>{t('sidebar.add_packaging', 'Ajouter Conditionnement')}</span> </button>)}
        {canShow('art_gl', 'GESTOCK') && (<button className={`nav-item ${activeClass('/articles/history')}`} onClick={() => goTo('/articles/history')}><span>{t('sidebar.article_ledger', 'Grand livre Article')}</span></button>)}
        {canShow('art_tables', 'GESTOCK') && (<button className={`nav-item ${activeClass('/articles/tables')}`} onClick={() => goTo('/articles/tables')}><span>{t('sidebar.tables_management', 'Gestion des Tables')}</span></button>)}
      </>
    )
  );

const renderScolaireMenu = () => (
    canAccessModule('SCOOL') && (
      <>
        <BackBtn />
        <div className="menu-group-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <GraduationCap size={16} />
          <span>Gestion Scolaire</span>
        </div>
        {canShow('scol_annees_manage', 'SCOOL') && <button className={`nav-item ${activeClass('/scolaire/annees')}`} onClick={() => goTo('/scolaire/annees')}><span>Années Scolaires</span></button>}
        {canShow('scol_cycles_manage', 'SCOOL') && <button className={`nav-item ${activeClass('/scolaire/cycles')}`} onClick={() => goTo('/scolaire/cycles')}><span>Cycles Pédagogiques</span></button>}
        {canShow('scol_decoupages_manage', 'SCOOL') && <button className={`nav-item ${activeClass('/scolaire/decoupages-periodes')}`} onClick={() => goTo('/scolaire/decoupages-periodes')}><span>Découpages & Périodes</span></button>} {/* 👈 Ajouté ici */}
        {canShow('scol_niveaux_manage', 'SCOOL') && <button className={`nav-item ${activeClass('/scolaire/niveaux-classes')}`} onClick={() => goTo('/scolaire/niveaux-classes')}><span>Niveaux et Classes</span></button>}
        {canShow('scol_matieres_manage', 'SCOOL') && <button className={`nav-item ${activeClass('/scolaire/matieres')}`} onClick={() => goTo('/scolaire/matieres')}><span>Matières & Affectations</span></button>}
        {canShow('scol_eleves_manage', 'SCOOL') && <button className={`nav-item ${activeClass('/scolaire/eleves')}`} onClick={() => goTo('/scolaire/eleves')}><span>Élèves et Tuteurs</span></button>}
        {canShow('scol_enseignants_manage', 'SCOOL') && <button className={`nav-item ${activeClass('/scolaire/enseignants')}`} onClick={() => goTo('/scolaire/enseignants')}><span>Enseignants</span></button>}
      </>
    )
  );

  const renderHebergementMenu = () => (
    canAccessModule('HEBERGEMENT') && (
      <>
        <BackBtn />
        <div className="menu-group-label">Hébergement & Hôtel</div>
        {canShow('heb_planning', 'HEBERGEMENT') && <button className={`nav-item ${activeClass('/hebergement/planning')}`} onClick={() => goTo('/hebergement/planning')}><span>Planning & Séjours</span></button>}
        {canShow('heb_nouvelle_res', 'HEBERGEMENT') && <button className={`nav-item ${activeClass('/hebergement/nouvelle-reservation')}`} onClick={() => goTo('/hebergement/nouvelle-reservation')}><span>Nouvelle Réservation</span></button>}
        {canShow('heb_historique_res', 'HEBERGEMENT') && <button className={`nav-item ${activeClass('/hebergement/historique-reservations')}`} onClick={() => goTo('/hebergement/historique-reservations')}><span>Historique Réservations</span></button>}
        {canShow('heb_checkin_checkout', 'HEBERGEMENT') && <button className={`nav-item ${activeClass('/hebergement/checkin')}`} onClick={() => goTo('/hebergement/checkin')}><span>Check-in Walkin</span></button>}
        {canShow('heb_composition_cat', 'HEBERGEMENT') && <button className={`nav-item ${activeClass('/hebergement/Checkout')}`} onClick={() => goTo('/hebergement/Checkout')}><span>Checkout</span></button>}
        <hr style={{ border: '0.1px solid #334155', margin: '4px 0' }} />
       {canShow('heb_extras_config', 'HEBERGEMENT') && <button className={`nav-item ${activeClass('/hebergement/extras')}`} onClick={() => goTo('/hebergement/extras')}><span>Configuration Extras</span></button>}
        {canShow('heb_poster_extras', 'HEBERGEMENT') && <button className={`nav-item ${activeClass('/hebergement/poster-extras')}`} onClick={() => goTo('/hebergement/poster-extras')}><span>Guichet Vente Extras</span></button>}   
        {canShow('heb_historique_extras', 'HEBERGEMENT') && <button className={`nav-item ${activeClass('/hebergement/historique-extras')}`} onClick={() => goTo('/hebergement/historique-extras')}><span>Historique Extras</span></button>}
        <hr style={{ border: '0.1px solid #334155', margin: '4px 0' }} />
        {canShow('heb_encaissements', 'HEBERGEMENT') && <button className={`nav-item ${activeClass('/hebergement/encaissements')}`} onClick={() => goTo('/hebergement/encaissements')}><span>Encaissements & Règlements</span></button>}
        <hr style={{ border: '0.1px solid #334155', margin: '4px 0' }} />
        {canShow('heb_categories', 'HEBERGEMENT') && <button className={`nav-item ${activeClass('/hebergement/categories')}`} onClick={() => goTo('/hebergement/categories')}><span>Catégories Chambres</span></button>}
        {canShow('heb_composants', 'HEBERGEMENT') && <button className={`nav-item ${activeClass('/hebergement/composants')}`} onClick={() => goTo('/hebergement/composants')}><span>Prestations</span></button>}
        {canShow('heb_chambres', 'HEBERGEMENT') && <button className={`nav-item ${activeClass('/hebergement/chambres')}`} onClick={() => goTo('/hebergement/chambres')}><span>Chambres Physiques</span></button>}
        {canShow('heb_regles_tarifs', 'HEBERGEMENT') && <button className={`nav-item ${activeClass('/hebergement/regles-tarifs')}`} onClick={() => goTo('/hebergement/regles-tarifs')}><span>Règles Tarifs Horaires</span></button>}
        {canShow('heb_parametres_horaires', 'HEBERGEMENT') && <button className={`nav-item ${activeClass('/hebergement/parametres-horaires')}`} onClick={() => goTo('/hebergement/parametres-horaires')}><span>Paramètres Horaires & No-Show</span></button>}
        {canShow('heb_civilites', 'HEBERGEMENT') && <button className={`nav-item ${activeClass('/hebergement/civilites')}`} onClick={() => goTo('/hebergement/civilites')}><span>Civilités</span></button>}
        {canShow('heb_societes', 'HEBERGEMENT') && <button className={`nav-item ${activeClass('/hebergement/societes')}`} onClick={() => goTo('/hebergement/societes')}><span>Gestion Sociétés</span></button>}
      </>
    )
  );

  const renderEmballageMenu = () => (
    canAccessModule('EMBALLAGE') && (
      <>
        <BackBtn />   
        {canShow('emb_create', 'EMBALLAGE') && (<button className={`nav-item ${activeClass('/emballage/create')}`} onClick={() => goTo('/emballage/create')}>{t('sidebar.add_emballage', 'Création Emballage')}</button>)}
        {canShow('emb_achat', 'EMBALLAGE') && (<button className={`nav-item ${activeClass('/emballage/achat')}`} onClick={() => goTo('/emballage/achat')}>{t('sidebar.emballage_Achat', 'Achat Emballages')}</button>)}
        {canShow('emb_regles', 'EMBALLAGE') && (<button className={`nav-item ${activeClass('/emballage/regles-consignation')}`} onClick={() => goTo('/emballage/regles-consignation')}>{t('sidebar.regles_consignation', 'Configuration des Flux')}</button>)}
        {canShow('emb_consignation', 'EMBALLAGE') && (<button className={`nav-item ${activeClass('/emballages/consignation')}`} onClick={() => goTo('/emballages/consignation')}>{t('sidebar.emballage_consignation', 'Consignation Emballages')}</button>)} 
        {canShow('emb_history', 'EMBALLAGE') && (<button className={`nav-item ${activeClass('/emballage/historique-flux-emballage')}`} onClick={() => goTo('/emballage/historique-flux-emballage')}>{t('sidebar.Embalage_flux_historique', 'Historique des inventaires')}</button>)}        
        {canShow('emb_inventory_suivi', 'EMBALLAGE') && (<button className={`nav-item ${activeClass('/emballages/suivi')}`} onClick={() => goTo('/emballages/suivi')}>{t('sidebar.emballage_inventory_suivi', 'Suivi & Lancement Inv.')}</button>)}
      </>
    )
  );
  

const renderVenteMenu = () => (
    canAccessModule('GESTOCK') && (
      <>
        <BackBtn />
        <div className="menu-group-label">{t('sidebar.pos_terminal', 'Terminal de Vente POS')}</div>
        {canShow('pos_vente_create', 'GESTOCK') && <button className={`nav-item ${activeClass('/pos')}`} onClick={() => goTo('/pos')}><span>{t('sidebar.counter_sale', 'Vente au Comptoir')}</span></button>}
        {canShow('pos_add', 'GESTOCK') && <button className={`nav-item ${activeClass('/pos/add')}`} onClick={() => goTo('/pos/add')}><span>{t('sidebar.cart_sale', 'Vente Au panier')}</span></button>}
        {canShow('pos_validate', 'GESTOCK') && <button className={`nav-item ${activeClass('/pos/validate')}`} onClick={() => goTo('/pos/validate')}><span>{t('sidebar.validate_cart', 'Valider un Panier')}</span></button>}
        
        {/* 🚀 1. POS RESTAURANT (AVEC PRODUCTION) */}
        {canShow('pos_vente_create', 'GESTOCK') && (
          <button className={`nav-item ${activeClass('/pos/restaurant')}`} onClick={() => goTo('/pos/restaurant')} style={{ color: '#38bdf8', fontWeight: 'bold' }}>
            <span>{t('sidebar.pos_restaurant', 'POS Restaurant & Menus')}</span>
          </button>
        )}

        {/* 🚀 2. POS RESTAURANT (SANS PRODUCTION / VSP) */}
        {canShow('vsp_vente_create', 'GESTOCK') && (
          <button className={`nav-item ${activeClass('/pos/restaurant')}`} onClick={() => goTo('/pos/restaurant')} style={{ color: '#34d399', fontWeight: 'bold' }}>
            <span>{t('sidebar.pos_restaurant_vsp', 'POS Restaurant (Sans Production)')}</span>
          </button>
        )}

        {canShow('pos_invoice', 'GESTOCK') && <button className={`nav-item ${activeClass('/pos/invoice')}`} onClick={() => goTo('/pos/invoice')}><span>{t('sidebar.standardized_invoice', 'Vente Normalisée')}</span></button>}
        {canShow('pos_vente_grille', 'GESTOCK') && <button className={`nav-item ${activeClass('/pos/commerciale-clients')}`} onClick={() => goTo('/pos/commerciale-clients')}><span>{t('sidebar.standardized_grille', 'Commerciale')}</span></button>}
        {canShow('pos_vente_liste', 'GESTOCK') && <button className={`nav-item ${activeClass('/pos/tourneesCommerciales-clients')}`} onClick={() => goTo('/pos/tourneesCommerciales-clients')}><span>{t('sidebar.standardized_tournees', 'Liste Commerciale')}</span></button>}
        {canShow('pos_history', 'GESTOCK') && <button className={`nav-item ${activeClass('/pos/history')}`} onClick={() => goTo('/pos/history')}><span>{t('sidebar.sales_history', 'Historique des ventes')}</span></button>}
        {canShow('pos_details', 'GESTOCK') && <button className={`nav-item ${activeClass('/pos-details')}`} onClick={() => goTo('/pos-details')}><span>{t('sidebar.detailed_sales', 'Rapport des ventes')}</span></button>}
        {canShow('pos_creances_clients', 'GESTOCK') && <button className={`nav-item ${activeClass('/pos/creances-clients')}`} onClick={() => goTo('/pos/creances-clients')}><span>{t('sidebar.customer_debts', 'Suivi des créances')}</span></button>}
      </>
    )
  );

  const renderCaisseMenu = () => (
    <>
      <BackBtn />
      <div className="menu-group-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <Wallet size={16} />
        <span>{t('sidebar.caisse', 'Gestion Caisse')}</span>
      </div>
      {canShow('caisse_close', 'SYSTEM') && <button className={`nav-item ${activeClass('/pos/close')}`} onClick={() => goTo('/pos/close')}><span>{t('sidebar.shift_closing', 'Clôture de la Caisse')}</span></button>} 
      {canShow('caisse_history_cloture', 'SYSTEM') && <button className={`nav-item ${activeClass('/pos/historique-cloture')}`} onClick={() => goTo('/pos/historique-cloture')}><span>{t('sidebar.closing_history', 'Historique des clôtures')}</span></button>}
      {canShow('caisse_methodes', 'SYSTEM') && <button className={`nav-item ${activeClass('/pos/paiements/methodes')}`} onClick={() => goTo('/pos/paiements/methodes')}><span>{t('sidebar.add_payment_method', 'Ajouter un Moyen de Paiement')}</span></button>}
    </>
  );

  const renderTiersMenu = () => (
    <>
      <BackBtn />
      <div className="menu-group-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <Users size={16} />
        <span>{t('sidebar.tiers', 'Gestion Tiers')}</span>
      </div>
      {canShow('tiers_fournisseur', 'SYSTEM') && <button className={`nav-item ${activeClass('/Tiers/fournisseurs')}`} onClick={() => goTo('/Tiers/fournisseurs')}><span>{t('sidebar.tiers_fournisseur', 'Enregistrer un Fournisseur')}</span></button>}
      {canShow('tiers_client', 'SYSTEM') && <button className={`nav-item ${activeClass('/Tiers/clients')}`} onClick={() => goTo('/Tiers/clients')}><span>{t('sidebar.tiers_client', 'Enregistrer un Client')}</span></button>}
    </>
  );

  const renderRapportsManagerMenu = () => (
    <>
      <BackBtn />
      <div className="menu-group-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <BarChart3 size={16} />
        <span>Rapports Manager</span>
      </div>
      <button className={`nav-item ${activeClass('/rapports/ca')}`} onClick={() => goTo('/rapports/ca')}><span>Rapport CA</span></button>
      <button className={`nav-item ${activeClass('/rapports/encaissements')}`} onClick={() => goTo('/rapports/encaissements')}><span>Rapport Encaissement</span></button>
      <button className={`nav-item ${activeClass('/rapports/night-audit')}`} onClick={() => goTo('/rapports/night-audit')}><span>Rapport Night Audit</span></button>
    </>
  );

  const renderLogistiqueMenu = () => (
    canAccessModule('GESTOCK') && (
      <>
        <BackBtn />
        <div className="menu-group-label">{t('sidebar.purchasing_logistics', 'Achat & Logistique')}</div>
        {canShow('log_buy', 'GESTOCK') && <button className={`nav-item ${activeClass('/logistique/achat')}`} onClick={() => goTo('/logistique/achat')}>{t('sidebar.make_purchase', 'Effectuer un Achat')}</button>}
        {canShow('log_history', 'GESTOCK') && <button className={`nav-item ${activeClass('/logistique/hist-achats')}`} onClick={() => goTo('/logistique/hist-achats')}>{t('sidebar.purchase_history', 'Historique Achats')}</button>}
        {canShow('log_inventory', 'GESTOCK') && <button className={`nav-item ${activeClass('/logistique/inventaire')}`} onClick={() => goTo('/logistique/inventaire')}>{t('sidebar.inventory', 'Inventaire')}</button>}
        {canShow('log_inventory_hist', 'GESTOCK') && <button className={`nav-item ${activeClass('/logistique/historique-inventaire')}`} onClick={() => goTo('/logistique/historique-inventaire')}>{t('sidebar.inventory_history', 'Hist. Inventaires')}</button>}
        {canShow('log_bon_commande', 'GESTOCK') && <button className={`nav-item ${activeClass('/logistique/bon-commande')}`} onClick={() => goTo('/logistique/bon-commande')}>{t('sidebar.make_order', 'Passer une Commande')}</button>}
        {canShow('log_historique_bon', 'GESTOCK') && <button className={`nav-item ${activeClass('/logistique/historique-bon')}`} onClick={() => goTo('/logistique/historique-bon')}>{t('sidebar.order_history', 'Historique des Commandes')}</button>}
        {canShow('log_ajustement', 'GESTOCK') && <button className={`nav-item ${activeClass('/logistique/ajustements')}`} onClick={() => goTo('/logistique/ajustements')}>{t('sidebar.stock_adjustments', 'Régularisation du Stock')}</button>}
        {canShow('log_historique_ajustement', 'GESTOCK') && <button className={`nav-item ${activeClass('/logistique/historique-ajustements')}`} onClick={() => goTo('/logistique/historique-ajustements')}>{t('sidebar.stock_adjustments_history', 'Historique des Régularisations')}</button>}
        {canShow('log_suppliers_dettes', 'GESTOCK') && <button className={`nav-item ${activeClass('/logistique/dettes-fournisseurs')}`} onClick={() => goTo('/logistique/dettes-fournisseurs')}>{t('sidebar.supplier_debts', 'Suivi des dettes')}</button>}
      </>
    )
  );

  const renderRHMenu = () => (            
    <>
      <BackBtn />
      <div className="menu-group-label">{t('sidebar.user_management', "Gestion d'utilisateurs")}</div>    
      {canShow('user_create', 'SYSTEM') && <button className={`nav-item ${activeClass('/admin/users')}`} onClick={() => goTo('/admin/users')}>{t('sidebar.user_accounts', 'Utilisateurs & Accès')}</button>}
      {canShow('staff_manage', 'SYSTEM') && <button className={`nav-item ${activeClass('/admin/staff')}`} onClick={() => goTo('/admin/staff')}>{t('sidebar.staff_management', 'Gestion du Staff')}</button>}
    </>
  );

  const renderComptaMenu = () => (
    canAccessModule('COMPTA_BASE') && (
      <>
        <BackBtn />
        <div className="menu-group-label" style={{ marginTop: '15px' }}>{t('sidebar.general_accounting', 'COMPTABILITÉ GÉNÉRALE')}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {canShow('compta_ex', 'COMPTA_BASE') && <button className={`nav-item ${activeClass('/compta/exercices')}`} onClick={() => goTo('/compta/exercices')}>{t('sidebar.fiscal_years', 'Exercices Comptables')}</button>}
          {canShow('compta_jr', 'COMPTA_BASE') && <button className={`nav-item ${activeClass('/compta/journaux')}`} onClick={() => goTo('/compta/journaux')}>{t('sidebar.journal_codes', 'Codes Journaux')}</button>}
          {canShow('compta_plan', 'COMPTA_BASE') && <button className={`nav-item ${activeClass('/compta/plan')}`} onClick={() => goTo('/compta/plan')}>{t('sidebar.chart_of_accounts_btn', 'Plan Comptable')}</button>}
          {canShow('analytique_plan', 'COMPTA_BASE') && <button className={`nav-item ${activeClass('/compta/Analytique')}`} onClick={() => goTo('/compta/Analytique')}>{t('sidebar.analytic_plan_btn', 'Plan Analytique')}</button>}
          {canShow('analytique_plan', 'COMPTA_BASE') && <button className={`nav-item ${activeClass('/compta/repartitions/config')}`} onClick={() => goTo('/compta/repartitions/config')}>{t('sidebar.analytic_config', 'Config Analytique')}</button>}
          {canShow('compta_tiers', 'COMPTA_BASE') && <button className={`nav-item ${activeClass('/compta/tiers')}`} onClick={() => goTo('/compta/tiers')}>{t('sidebar.third_party_plan', 'Plan des Tiers')}</button>}
          {canShow('compta_ecritures_auto', 'COMPTA_BASE') && <button className={`nav-item ${activeClass('/compta/config-ecritures-auto')}`} onClick={() => goTo('/compta/config-ecritures-auto')}>{t('sidebar.auto_entries_settings', 'Paramètres Écritures Auto')}</button>}
        </div> 

        <div className="menu-group-label" style={{ marginTop: '15px' }}>{t('sidebar.daily_entry_section', 'OPERATIONS DE SAISIES')}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {canShow('compta_brouillon', 'COMPTA_BASE') && <button className={`nav-item ${activeClass('/compta/brouillon-selection')}`} onClick={() => goTo('/compta/brouillon-selection')}>{t('sidebar.assistant_entry_btn', 'Journal Brouillon')}</button>}
          {canShow('compta_val', 'COMPTA_BASE') && <button className={`nav-item ${activeClass('/compta/validation')}`} onClick={() => goTo('/compta/validation')}>{t('sidebar.drafts_validation', 'Validation Brouillons')}</button>}
          {canShow('compta_gen', 'COMPTA_BASE') && <button className={`nav-item ${activeClass('/compta/gen')}`} onClick={() => goTo('/compta/gen')}>{t('sidebar.daily_entry_btn', 'Saisie Journalière')}</button>}
          {canShow('compta_treso_ventilation', 'COMPTA_BASE') && <button className={`nav-item ${activeClass('/compta/treso/ventilation')}`} onClick={() => goTo('/compta/treso/ventilation')}>{t('sidebar.ledger_ventilation', 'Ventilation du Brouillard')}</button>}
          {canShow('compta_cloture', 'COMPTA_BASE') && <button className={`nav-item ${activeClass('/compta/cloture-journaliere')}`} onClick={() => goTo('/compta/cloture-journaliere')}>{t('sidebar.daily_closing_btn', 'Clôture Journalière')}</button>}
        </div> 

        <div className="menu-group-label" style={{ marginTop: '15px' }}>{t('sidebar.treasury', 'TRÉSORERIE')}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {canShow('compta_treso_saisie_hub', 'COMPTA_BASE') && <button className={`nav-item ${activeClass('/compta/treso/choix-brouillard')}`} onClick={() => goTo('/compta/treso/choix-brouillard')}>{t('sidebar.cash_drafts', 'Brouillard de saisie')}</button>}
          {canShow('compta_treso_val', 'COMPTA_BASE') && <button className={`nav-item ${activeClass('/compta/treso/validation')}`} onClick={() => goTo('/compta/treso/validation')}>{t('sidebar.expense_validation', 'Valider une Dépense')}</button>}
          {canShow('compta_brouillard_config', 'COMPTA_BASE') && <button className={`nav-item ${activeClass('/compta/type-brouillards')}`} onClick={() => goTo('/compta/type-brouillards')}>{t('sidebar.draft_config', 'Configuration du Brouillard')}</button>}
        </div> 

        <div className="menu-group-label" style={{ marginTop: '15px' }}>{t('sidebar.reports_statements', 'RAPPORTS & ÉTATS')}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {canShow('rpt_bal_comptes', 'COMPTA_BASE') && <button className={`nav-item ${activeClass('/compta/rapports/balance-comptes')}`} onClick={() => goTo('/compta/rapports/balance-comptes')}>{t('sidebar.accounts_balance', 'Balance des comptes')}</button>}
          {canShow('rpt_bal_tiers', 'COMPTA_BASE') && <button className={`nav-item ${activeClass('/compta/rapports/balance-tiers')}`} onClick={() => goTo('/compta/rapports/balance-tiers')}>{t('sidebar.third_party_balance_btn', 'Balance des tiers')}</button>}
          {canShow('compta_rpt_bal_agee', 'COMPTA_BASE') && <button className={`nav-item ${activeClass('/compta/rapports/balance-agee')}`} onClick={() => goTo('/compta/rapports/balance-agee')}>{t('sidebar.aged_balance', 'Balance âgée')}</button>}
          {canShow('compta_rpt_bal_ana', 'COMPTA_BASE') && <button className={`nav-item ${activeClass('/compta/rapports/balance-analytique')}`} onClick={() => goTo('/compta/rapports/balance-analytique')}>{t('sidebar.analytic_balance', 'Balance analytique')}</button>}
          <hr style={{ border: '0.1px solid #ced4db', margin: '4px 0', opacity: 0.3 }} />
          {canShow('compta_rpt_gl_comptes', 'COMPTA_BASE') && <button className={`nav-item ${activeClass('/compta/rapports/grand-livre')}`} onClick={() => goTo('/compta/rapports/grand-livre')}>{t('sidebar.general_ledger_btn', 'Grand livre des comptes')}</button>}
          {canShow('compta_rpt_gl_tiers', 'COMPTA_BASE') && <button className={`nav-item ${activeClass('/compta/rapports/grand-livre-tiers')}`} onClick={() => goTo('/compta/rapports/grand-livre-tiers')}>{t('sidebar.third_party_ledger_btn', 'Grand livre des tiers')}</button>}
          {canShow('compta_rpt_gl_ana', 'COMPTA_BASE') && <button className={`nav-item ${activeClass('/compta/rapports/grand-livre-analytique')}`} onClick={() => goTo('/compta/rapports/grand-livre-analytique')}>{t('sidebar.analytic_ledger_btn', 'Grand livre analytique')}</button>}
          {canShow('compta_rpt_central', 'COMPTA_BASE') && <button className={`nav-item ${activeClass('/compta/rapports/central')}`} onClick={() => goTo('/compta/rapports/central')}>Journal Centralisateur</button>}
          <hr style={{ border: '0.1px solid #ced6e2', margin: '4px 0', opacity: 0.3 }} />
          {canShow('compta_rpt_bilan', 'COMPTA_BASE') && <button className={`nav-item ${activeClass('/compta/rapports/bilan')}`} onClick={() => goTo('/compta/rapports/bilan')}>{t('sidebar.balance_sheet_btn', 'Bilan')}</button>}
          {canShow('rpt_resultat', 'COMPTA_BASE') && <button className={`nav-item ${activeClass('/compta/rapports/resultat')}`} onClick={() => goTo('/compta/rapports/resultat')}>{t('sidebar.income_statement', 'Compte de résultat')}</button>}
          {canShow('rpt_tft', 'COMPTA_BASE') && <button className={`nav-item ${activeClass('/compta/rapports/tft')}`} onClick={() => goTo('/compta/rapports/tft')}>{t('sidebar.cash_flow_statement', 'TFT (Flux Tréso)')}</button>}
          {canShow('compta_etats_recap', 'COMPTA_BASE') && <button className={`nav-item ${activeClass('/compta/rapports/etats-financiers')}`} onClick={() => goTo('/compta/rapports/etats-financiers')}>{t('sidebar.financial_statements_recap', 'États Financiers Recap')}</button>}
        </div>
      </>
    )
  );

  const renderAnalytiqueMenu = () => (
    canAccessModule('PRODUCTION') && (
      <>
        <BackBtn />
        <div className="menu-group-label" style={{ marginTop: '15px' }}>STOCKS PRODUCTION</div>
        {canShow('analytique_mp_categories', 'PRODUCTION') && <button className={`nav-item ${activeClass('/analytique/mp/categories')}`} onClick={() => goTo('/analytique/mp/categories')}>Catégories Matières Premières</button>}
        {canShow('analytique_mp', 'PRODUCTION') && <button className={`nav-item ${activeClass('/analytique/mp/liste')}`} onClick={() => goTo('/analytique/mp/liste')}>{t('sidebar.analytique_liste', 'Liste matière première')}</button>}
        {canShow('analytique_mp', 'PRODUCTION') && <button className={`nav-item ${activeClass('/analytique/mp/achat')}`} onClick={() => goTo('/analytique/mp/achat')}>{t('sidebar.analytique_achat', 'Achat MP')}</button>}
        {canShow('analytique_mp', 'PRODUCTION') && <button className={`nav-item ${activeClass('/analytique/mp/historique')}`} onClick={() => goTo('/analytique/mp/historique')}>{t('sidebar.analytique_history_achat', 'Historique Achat MP')}</button>}
        {canShow('analytique_mp_inventaire', 'PRODUCTION') && <button className={`nav-item ${activeClass('/analytique/mp/inventaire')}`} onClick={() => goTo('/analytique/mp/inventaire')}>Inventaire MP</button>}
        {canShow('analytique_psf_hist', 'PRODUCTION') && <button className={`nav-item ${activeClass('/analytique/psf')}`} onClick={() => goTo('/analytique/psf')}>{t('sidebar.Liste_psf', 'Liste des PSF')}</button>}
        {canShow('analytique_psf_inventaire', 'PRODUCTION') && <button className={`nav-item ${activeClass('/analytique/psf/inventaire')}`} onClick={() => goTo('/analytique/psf/inventaire')}>Inventaire PSF</button>}
        {canShow('analytique_psf_hist', 'PRODUCTION') && <button className={`nav-item ${activeClass('/analytique/psf/historique')}`} onClick={() => goTo('/analytique/psf/historique')}>{t('sidebar.psf_history', 'Historique & Traçabilité')}</button>}
        {canShow('analytique_ft', 'PRODUCTION') && <button className={`nav-item ${activeClass('/analytique/ft/creation')}`} onClick={() => goTo('/analytique/ft/creation')}>{t('sidebar.psf_creation', 'Fiches Techniques')}</button>}
        {canShow('prod_sur_mesure', 'PRODUCTION') && <button className={`nav-item ${activeClass('/analytique/production/sur-mesure')}`} onClick={() => goTo('/analytique/production/sur-mesure')}>Vente Sur-Mesure</button>}
        
        {canShow('prod_magasin_verif', 'PRODUCTION') && <button className={`nav-item ${activeClass('/analytique/production/consommation-departement')}`} onClick={() => goTo('/analytique/production/consommation-departement')}>Consommation Directe</button>}
        {canShow('prod_magasin_hist_conso', 'PRODUCTION') && <button className={`nav-item ${activeClass('/analytique/production/consommation-departement-historique')}`} onClick={() => goTo('/analytique/production/consommation-departement-historique')}>Historique Conso. Départements</button>}
        {canShow('prod_besoin', 'PRODUCTION') && <button className={`nav-item ${activeClass('/analytique/production/besoin')}`} onClick={() => goTo('/analytique/production/besoin')}>Émettre un Besoin</button>}
        {canShow('prod_enregistrer_commande', 'PRODUCTION') && <button className={`nav-item ${activeClass('/analytique/production/enregistrer-commande')}`} onClick={() => goTo('/analytique/production/enregistrer-commande')}>Enregistrement Commande</button>}
        {canShow('prod_magasin_verif', 'PRODUCTION') && <button className={`nav-item ${activeClass('/analytique/production/magasin')}`} onClick={() => goTo('/analytique/production/magasin')}>Vérification Stock</button>}
        {canShow('prod_magasin_reception', 'PRODUCTION') && <button className={`nav-item ${activeClass('/analytique/production/reception')}`} onClick={() => goTo('/analytique/production/reception')}>Magasinage & Réception</button>}
        {canShow('prod_pilotage', 'PRODUCTION') && <button className={`nav-item ${activeClass('/analytique/production/pilotage')}`} onClick={() => goTo('/analytique/production/pilotage')}>Pilotage & Lancements</button>}
        {canShow('prod_regularisations', 'PRODUCTION') && <button className={`nav-item ${activeClass('/analytique/production/regularisations-attente')}`} onClick={() => goTo('/analytique/production/regularisations-attente')}>Régul. Stocks Attente</button>}
        {canShow('prod_magasin_hist_conso', 'PRODUCTION') && (
          <button className={`nav-item ${activeClass('/analytique/production/historique-consommations')}`} onClick={() => goTo('/analytique/production/historique-consommations')}>
            <History size={16} style={{ marginRight: '8px' }} />
            <span>Historique des Consommations</span>
          </button>
        )}
      </>
    )
  );

  const renderSettingsMenu = () => (
    <>
      <BackBtn />
      <div className="menu-group-label">{t('sidebar.company_settings', 'Paramètres de la Société')}</div>
      {canShow('params_edit_settings', 'SYSTEM') && (<button className={`nav-item ${activeClass('/admin/settings')}`} onClick={() => goTo('/admin/settings')}><div className="nav-item-content"><Settings2 size={20} /><span>{t('sidebar.company_info', 'Informations Société')}</span></div></button>)}
      {canShow('params_view_licence', 'SYSTEM') && (<button className={`nav-item ${activeClass('/admin/licence')}`} onClick={() => goTo('/admin/licence')}><div className="nav-item-content"><ShieldCheck size={20} /><span>{t('sidebar.my_license', 'Ma Licence')}</span></div></button>)}
      {canShow('params_view_audit', 'SYSTEM') && (<button className={`nav-item ${activeClass('/audit')}`} onClick={() => goTo('/audit')}><div className="nav-item-content"><Activity size={20} /><span>{t('sidebar.audit_log', 'Journal des actions')}</span></div></button>)}
      {canShow('params_cloud_push', 'SYSTEM') && (<button className="nav-item" onClick={handleManualPush} disabled={isPushing}><div className="nav-item-content">{isPushing ? <RefreshCcw size={20} className="animate-spin" /> : <TrendingUp size={20} color="#2563eb" />}<span style={{ color: '#2563eb', fontWeight: 'bold' }}>{t('sidebar.cloud_backup', 'Sauvegarder vers Cloud')}</span></div></button>)}
      {canShow('params_cloud_restore', 'SYSTEM') && (<button className="nav-item" onClick={handleCloudRestore} disabled={isRestoring}><div className="nav-item-content">{isRestoring ? <RefreshCcw size={20} className="animate-spin" /> : <RefreshCcw size={20} color="#c2410c" />}<span style={{ color: '#c2410c', fontWeight: 'bold' }}>{t('sidebar.cloud_restore', 'Restauration Cloud')}</span></div></button>)}
    </>
  );

  const renderMainMenu = () => (
    <>
      <div className="menu-group-label">{t('sidebar.dashboard_upper', 'TABLEAU DE BORD')}</div>
      <button className={`nav-item ${activeClass('/admin/dashboard')}`} onClick={() => goTo('/admin/dashboard')}><div className="nav-item-content"><LayoutDashboard size={20}/> <span>{t('sidebar.dashboard', 'Dashboard')}</span></div></button>
    </>
  );

  const handleFermerSessionEtRetour = () => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = '#/login';
    window.location.reload();
  };

  const renderCurrentMenu = () => {
    if (isArticleSection) return renderArticleMenu();
    if (isHebergementSection) return renderHebergementMenu();
    if (isCaisseSection) return renderCaisseMenu();
    if (isTiersSection) return renderTiersMenu();
    if (isRapportsManagerSection) return renderRapportsManagerMenu();
    if (isVenteSection) return renderVenteMenu();
    if (isLogistiqueSection) return renderLogistiqueMenu();
    if (isAnalytiqueSection) return renderAnalytiqueMenu();
    if (isComptaSection) return renderComptaMenu();
    if (isRHSection) return renderRHMenu();
    if (isScolaireSection) return renderScolaireMenu();
    if (isEmballageSection) return renderEmballageMenu();
    if (isSettingsSection) return renderSettingsMenu();
    return renderMainMenu(); 
  };

  return (
    <>
      <button 
        className="mobile-burger-toggle" 
        onClick={() => setIsMobileOpen(!isMobileOpen)}
        aria-label="Menu"
      >
        {isMobileOpen ? <X size={22} /> : <Menu size={22} />}
      </button>

      {isMobileOpen && (
        <div 
          className="mobile-sidebar-overlay" 
          onClick={() => setIsMobileOpen(false)} 
        />
      )}

      <aside className={`sidebar ${isMobileOpen ? 'mobile-open' : ''}`} style={{ position: 'relative' }}>
        <style>{scrollbarStyles}</style>
        <div className="sidebar-header">
          <div className="logo-m">{companyName.charAt(0).toUpperCase()}</div>
          <div className="company-info">
            <span className="company-name">{companyName}</span>
            <span 
              className="status-badge" 
              style={{
                backgroundColor: licenseData.valid ? '#10b981' : '#52ef44',
                color: '#ffffff',
                padding: '2px 8px',
                borderRadius: '12px',
                fontSize: '11px',
                fontWeight: '700',
                marginTop: '4px',
                display: 'inline-block',
                textAlign: 'center'
              }}
            >
              {licenseData.valid ? t('dashboard.licence.active', '') : t('dashboard.licence.invalid', '')}
            </span>
          </div>
        </div>
        <div className="scroll-arrow-box">
          <button onClick={() => scrollManual('up')} className="arrow-btn">
            <ChevronUp size={16}/>
          </button>
          <button onClick={() => scrollManual('down')} className="arrow-btn">
            <ChevronDown size={16}/>
          </button>
        </div>
        <nav 
          className="sidebar-nav" 
          ref={scrollRef} 
          onScroll={handleScroll}
          style={{marginTop: '20px', flex: 1, overflowY: 'auto', paddingRight: '5px'}}
        >
          {renderCurrentMenu()}
        </nav>
        <div className="sidebar-footer">
          <button className="logout-button" onClick={handleFermerSessionEtRetour}>
            <LogOut size={18} />
            <span>{t('sidebar.sidebar_logout', 'Fermer la session')}</span>
          </button>
        </div>
      </aside>
    </>
  );
};

const scrollbarStyles = `
  .sidebar-nav {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  .sidebar-nav::-webkit-scrollbar {
    width: 10px;            
    display: block !important;
  }
  .sidebar-nav::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 10px;
  }
  .sidebar-nav::-webkit-scrollbar-thumb {
    background: #1e293b;      
    border-radius: 10px;
    border: 2px solid #f1f5f9;
  }
  .nav-item {
    display: flex !important;
    align-items: center;
    justify-content: flex-start;
    height: auto !important;
    min-height: 38px;
    white-space: normal !important;
    word-break: break-word;
    padding: 10px 14px;
  }
  .nav-item span {
    display: inline-block;
    line-height: 1.3;
    text-align: left;
  }
  .nav-item.active {
    background: #f8fafc !important;
    border-right: 5px solid #6366f1 !important; 
    color: #6366f1 !important;
    font-weight: 900 !important;
  }
  .scroll-arrow-box {
    position: absolute;
    right: 12px;
    top: 75px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    z-index: 1000;
  }
  .arrow-btn {
    background: #1e293b;
    color: white;
    border: none;
    border-radius: 4px;
    width: 26px;
    height: 26px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0.6;
    transition: 0.2s;
  }
  .arrow-btn:hover { opacity: 1; transform: scale(1.1); }
  
  .mobile-burger-toggle {
    display: none;
    position: fixed;
    top: 12px;
    left: 12px;
    z-index: 11000;
    background: #1e293b;
    color: #ffffff;
    border: none;
    border-radius: 8px;
    width: 40px;
    height: 40px;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
    cursor: pointer;
  }
  .mobile-sidebar-overlay {
    display: none;
  }
  
  @media (max-width: 768px) {
    .mobile-burger-toggle {
      display: flex !important;
    }
    .sidebar {
      position: fixed !important;
      top: 0;
      left: -280px;
      width: 260px;
      height: 100vh;
      z-index: 10000;
      transition: left 0.3s ease-in-out;
      box-shadow: 5px 0 25px rgba(0,0,0,0.3);
    }
    .sidebar.mobile-open {
      left: 0;
    }
    .mobile-sidebar-overlay {
      display: block;
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background: rgba(0, 0, 0, 0.5);
      z-index: 9999;
    }
  }
`;

export default Sidebar;